// src/pages/Users/Techniques/TechniquesView.jsx

import React, { useState } from "react";
import { FaBookOpen } from "react-icons/fa";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

// ✅ Chemin corrigé
import Reveal from "./../../../components/Reveal/Reveal";

import useTechniquesController from "./hooks/useTechniquesController";

import TechniquesHero from "./components/TechniquesHero";
import TechniquesToolbar from "./components/TechniquesToolbar";
import TechniqueCard from "./components/TechniqueCard";
import TechniqueSheet from "./components/TechniqueSheet";
import SkeletonCard from "./components/SkeletonCard";
import EmptyState from "./components/EmptyState";
import Uv2TrainingPanel from "./components/Uv2TrainingPanel";

export default function TechniquesView() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [opened, setOpened] = useState(null);

  const {
    loading,
    allTechniques,
    filtered,
    visible,
    favorites,
    ui,
    searchInput,
    setSearchInput,
    dynamicOptions,
    updateUi,
    resetFilters,
    loadMore,
    toggleFavorite,
    copyShareLink,
    shareCopied,
  } = useTechniquesController();

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
      <div className="hidden md:block">
        <ScrollIndicator />
      </div>

      {/* 
        ✅ Mobile conservé.
        ✅ Desktop augmenté pour éviter que le Hero / contenu soit trop proche de la navbar.
      */}
      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Techniques Judo"
          subtitle="Retrouve rapidement une technique, écoute sa prononciation japonaise et ajoute tes favoris."
          icon={FaBookOpen}
          align="left"
          className="mb-4"
        />

        <div className="mb-4">
          <DashboardNavigation role={userRole} />
        </div>

        <Reveal>
          <TechniquesHero
            totalCount={allTechniques.length}
            filteredCount={filtered.length}
            favoritesCount={favorites.length}
            showFav={ui.showFav}
          />
        </Reveal>

        <Reveal>
          <Uv2TrainingPanel
            allTechniques={allTechniques}
            onOpenTechnique={setOpened}
          />
        </Reveal>

        {/* 
          ✅ Toolbar sticky en mobile.
          ✅ Le panneau filtre s'ouvre maintenant sous la toolbar, 
          car il est intégré dans TechniquesToolbar et non plus en fixed.
        */}
        <div className="sticky top-[76px] z-30 -mx-3 mt-4 border-y border-slate-200/80 bg-slate-50/95 px-3 py-3 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/90 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-0">
          <TechniquesToolbar
            dynamicOptions={dynamicOptions}
            ui={ui}
            onChange={updateUi}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onReset={resetFilters}
            onShare={copyShareLink}
            shareCopied={shareCopied}
            filteredCount={filtered.length}
          />
        </div>

        <section className="mt-5">
          {loading && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {Array.from({ length: 12 }).map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <EmptyState onReset={resetFilters} />
          )}

          {!loading && filtered.length > 0 && (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {visible.map((technique) => (
                  <TechniqueCard
                    key={technique.id}
                    technique={technique}
                    isFavorite={favorites.includes(technique.id)}
                    onToggleFavorite={() => toggleFavorite(technique.id)}
                    onOpen={() => setOpened(technique)}
                  />
                ))}
              </div>

              {visible.length < filtered.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={loadMore}
                    className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/10 transition hover:-translate-y-0.5 hover:bg-slate-800 active:translate-y-0 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                  >
                    Charger plus de techniques
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>

      {opened && (
        <TechniqueSheet technique={opened} onClose={() => setOpened(null)} />
      )}
    </section>
  );
}
